<template>
  <v-card variant="outlined" rounded="lg" class="ac-section gm-card">
    <div class="ac-section__head d-flex flex-wrap align-start justify-space-between ga-3 pa-4">
      <div class="min-width-0">
        <h3 class="ac-section__title text-subtitle-1 font-weight-bold mb-3 d-flex align-center ga-2">
          <v-icon
            v-if="titleIcon"
            :icon="titleIcon"
            size="22"
            color="primary"
            class="ac-section__title-icon flex-shrink-0"
          />
          <span class="min-width-0">{{ title }}</span>
        </h3>
        <div class="d-flex flex-wrap align-center ga-4 text-body-2">
          <div class="d-flex align-center ga-2">
            <span class="text-medium-emphasis">设置结果</span>
            <span class="d-flex align-center ga-1" :class="syncStatus === 'synced' ? 'text-success' : 'text-error'">
              <span
                class="ac-section__dot"
                :class="syncStatus === 'synced' ? 'ac-section__dot--ok' : 'ac-section__dot--bad'"
              />
              {{ syncStatus === 'synced' ? '已同步' : '未同步' }}
            </span>
          </div>
          <div>
            <span class="text-medium-emphasis">上报时间</span>
            <span class="ml-2">{{ reportTime || '—' }}</span>
          </div>
        </div>
      </div>
      <div class="d-flex flex-wrap ga-2">
        <v-btn
          color="success"
          variant="flat"
          rounded="lg"
          class="text-none ac-section__action"
          prepend-icon="mdi-sync"
          @click="$emit('sync')"
        >
          空调策略同步
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          rounded="lg"
          class="text-none ac-section__action"
          prepend-icon="mdi-cog-outline"
          @click="$emit('settings')"
        >
          空调策略设置
        </v-btn>
        <v-btn
          variant="tonal"
          rounded="lg"
          class="text-none ac-section__action"
          prepend-icon="mdi-history"
          @click="$emit('history')"
        >
          空调参数设置历史
        </v-btn>
      </div>
    </div>

    <v-divider />

    <v-tabs v-model="innerTab" color="primary" density="comfortable" class="ac-section__tabs px-2">
      <v-tab value="current" class="text-none">当前数据</v-tab>
      <v-tab value="report" class="text-none">上报数据</v-tab>
      <!-- <v-tab value="compare" class="text-none">策略对比</v-tab> -->
    </v-tabs>

    <v-window v-model="innerTab" class="ac-section__window">
      <v-window-item value="current">
        <div class="pa-4 pt-2">
          <!-- <div class="text-body-2 font-weight-medium mb-3">当前数据</div> -->
          <template v-if="!hasData">
            <div class="ac-section__empty d-flex flex-column align-center justify-center py-10 rounded-lg">
              <div class="ac-section__empty-box rounded mb-3" />
              <span class="text-body-2 text-medium-emphasis">暂无数据</span>
            </div>
          </template>
          <template v-else>
            <div class="ac-section__summary rounded-lg pa-3">
              <blocks-summary-table :blocks="blocks" />
            </div>
          </template>
        </div>
      </v-window-item>

      <v-window-item value="report">
        <div class="pa-4 pt-2">
          <!-- <div class="text-body-2 font-weight-medium mb-3">上报数据</div> -->
          <template v-if="!reportBlocks.length">
            <div class="ac-section__empty d-flex flex-column align-center justify-center py-10 rounded-lg">
              <div class="ac-section__empty-box rounded mb-3" />
              <span class="text-body-2 text-medium-emphasis">暂无上报数据，请先完成「空调策略同步」</span>
            </div>
          </template>
          <template v-else>
            <div class="ac-section__summary rounded-lg pa-3">
              <blocks-summary-table :blocks="reportBlocks" />
            </div>
            <div class="text-caption text-medium-emphasis mt-3">以下为原始 JSON，便于联调核对。</div>
            <pre class="ac-section__json mt-2">{{ lastReportSnapshot }}</pre>
          </template>
        </div>
      </v-window-item>

      <v-window-item value="compare">
        <div class="pa-4 pt-2">
          <div class="text-body-2 font-weight-medium mb-3">当前策略与上报数据对比</div>
          <template v-if="!hasData && !reportBlocks.length">
            <div class="text-body-2 text-medium-emphasis py-6 text-center">暂无当前数据与上报数据，无法对比</div>
          </template>
          <template v-else-if="!reportBlocks.length">
            <div class="text-body-2 text-medium-emphasis py-6 text-center">
              暂无上报基线，请先保存参数并执行「空调策略同步」后再查看对比
            </div>
          </template>
          <template v-else-if="!hasData">
            <div class="text-body-2 text-medium-emphasis py-6 text-center">当前草稿无数据，请先在「空调策略设置」中保存参数</div>
          </template>
          <template v-else>
            <div class="ac-compare d-flex flex-column flex-lg-row ga-4">
              <div class="ac-compare__col flex-grow-1 min-width-0">
                <div class="text-caption font-weight-semibold mb-2 text-primary">当前策略</div>
                <div class="ac-section__summary rounded-lg pa-3">
                  <blocks-summary-table :blocks="blocks" />
                </div>
              </div>
              <div class="ac-compare__col flex-grow-1 min-width-0">
                <div class="text-caption font-weight-semibold mb-2 text-success">上报数据</div>
                <div class="ac-section__summary rounded-lg pa-3">
                  <blocks-summary-table :blocks="reportBlocks" />
                </div>
              </div>
            </div>
            <div v-if="compareHint" class="text-caption text-warning mt-3">{{ compareHint }}</div>
          </template>
        </div>
      </v-window-item>
    </v-window>
  </v-card>
</template>

<script setup>
import { computed, ref } from 'vue'
import BlocksSummaryTable from './BlocksSummaryTable.vue'

const props = defineProps({
  title: { type: String, required: true },
  /** 标题前 MDI 图标，如 mdi-access-point */
  titleIcon: { type: String, default: '' },
  syncStatus: { type: String, default: 'unsynced' },
  reportTime: { type: String, default: null },
  blocks: { type: Array, default: () => [] },
  /** 最近一次同步后的上报 JSON 字符串 */
  lastReportSnapshot: { type: String, default: null },
})

defineEmits(['sync', 'settings', 'history'])

const innerTab = ref('current')

const hasData = computed(() => Array.isArray(props.blocks) && props.blocks.length > 0)

function parseReportBlocks(raw) {
  if (raw == null || raw === '') return []
  try {
    const v = JSON.parse(raw)
    return Array.isArray(v) ? v : []
  } catch {
    return []
  }
}

const reportBlocks = computed(() => parseReportBlocks(props.lastReportSnapshot))

const compareHint = computed(() => {
  if (!hasData.value || !reportBlocks.value.length) return ''
  const a = JSON.stringify(props.blocks)
  const b = JSON.stringify(reportBlocks.value)
  if (a === b) return '当前草稿与最近一次上报数据一致。'
  return '当前草稿与上报数据存在差异，请以实际上位机/网关同步结果为准。'
})
</script>

<style scoped lang="scss">
.ac-section {
  border-color: #e9efff !important;
}

.ac-section__title {
  line-height: 1.35;
}

.ac-section__title-icon {
  opacity: 0.92;
}

.ac-section__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  &--bad {
    background: #e53935;
  }
  &--ok {
    background: var(--color-success, #36c070);
  }
}

.ac-section__action {
  letter-spacing: 0;
}

.ac-section__tabs {
  border-bottom: 1px solid #edf2f6;
}

.ac-section__window {
  min-height: 120px;
}

.ac-section__empty {
  border: 1px dashed #e0e4e7;
  background: #fafbff;
}

.ac-section__empty-box {
  width: 120px;
  height: 72px;
  border: 1px solid #e9efff;
  background: #fff;
}

.ac-section__summary {
  border: 1px solid #e9efff;
  background: #fff;
}

.ac-section__json {
  margin: 0;
  padding: 12px;
  background: #f4f6fc;
  border-radius: 8px;
  font-size: 11px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 200px;
  overflow: auto;
}

.ac-compare__col {
  min-width: 0;
}
</style>
